// Turns the `timeline/global` payload into the rows the Global timeline panel renders: today's
// events, the still-open dated tasks and the waiting-on emails, each with a live due status.
//
// The endpoint sends the due INSTANT and no status on purpose (its payload is cached) — the status
// is computed here against a `now` the caller passes in, so it advances on a ticking clock instead
// of freezing until the next reload. The same goes for how much a row is over due by / has left.
//
// Every date a row shows is its DUE date (issue #217 — see `toRow`), formatted in @/lib/timelineTime,
// never inline here: the exact date and time as the Figma "date and time-timeline" two-line variant
// draws them (`date` / `time`), and the same instant in the frame's own reading — "Today, 8pm" —
// on the labelled Due line the panel puts at the bottom of the row (`schedule`).
//
// A row reads THING-then-kind: the semibold first line is the item's own title (the task title, the
// email subject, the note title) with the status bell and how far over due / how long is left; the
// small second line names the KIND ("Task", "Email", "Web search"), and the row's date sits under
// them both.

import { statusMeta, statusExplanation, statusText, statusTokenColor, dueStatus, dueAmount, parseUtc } from '@/lib/dueStatus'
import { timelineDateTime, scheduleText, scheduleDay } from '@/lib/timelineTime'
// An EVENT is not late, it is over (busy-my-web#238/#239) — its state and its wording are that
// module's, and the Ongoing rail asks it the same two questions about the same event.
import { eventStatus, eventLabel, isPastEventStatus } from '@/lib/eventTime'
import { endOfDay } from '@/lib/taskDue'
import { toPlainText } from '@/lib/plainText'
// "Aug 18, 3 pm" — the exact instant a Due line stands for, in the app's one stamp wording
// (busy-my-web#501, 2026-09-15 parity audit F2-7).
import { factStamp } from '@/lib/briefings/taskFacts'

// Three type vocabularies exist in this app: the BFF's module strings ("email"|"task"|"event"|
// "note"), this panel's own strings ("message" for an email), and the numeric itemType the timeline
// store uses. The endpoint speaks the BFF's; the other two are mapped explicitly here so nothing
// downstream has to guess.
const PANEL_TYPE = {
  email: 'message', message: 'message', task: 'task', event: 'event', note: 'note',
  // A research row is a web search — the three words the app uses for one land on the same row type.
  research: 'research', search: 'research', websearch: 'research',
}
const ITEM_TYPE = { 1: 'message', 2: 'task', 3: 'event', 4: 'note' }

// What a row's SMALL second line says on the left: WHAT KIND of thing this is. Nouns, matching the
// module names the rest of the app uses (Emails / Tasks / Events / Notes); a research row reads
// "Web search". The item's own text leads the row above it.
const TYPE_TITLE = { message: 'Email', task: 'Task', event: 'Event', note: 'Note', research: 'Web search' }

// …and what the GLOBAL timeline's own kind-line says. The older frame (40006440:182480) had the noun
// take a "created" verb ("Event created") on the reading that every row there is the moment the thing
// was made; the newest home frame (40007749:343432) dropped it back to the plain kind word, and this
// reconciles to that (issue #65). #217 has since removed the premise as well as the verb — a row here
// is not a creation event at all, it is something that is due or already late, and its date says so
// (see `toRow`), so "Event created" would now be wrong twice over.
// The insight that survives from the older frame: an email or a web search was never "created" by the
// user, so neither ever took the verb — moot now that nothing does, but it's why this stays its own
// lookup rather than a bare alias of TYPE_TITLE.
const TYPE_CREATED = { ...TYPE_TITLE }

// The panel's icon/colour lookups key off these — anything unknown reads as an event.
export function panelType(type) {
  if (typeof type === 'number') return ITEM_TYPE[type] || 'event'
  return PANEL_TYPE[String(type || '').toLowerCase()] || 'event'
}

// The row's KIND label, from its type. Takes either vocabulary (the payload's "email" or the panel's
// "message"), so a caller never has to map first.
export function typeTitle(type) {
  return TYPE_TITLE[panelType(type)] || TYPE_TITLE.event
}

// The Global timeline's kind-line: the plain kind word ("Task", "Event", "Note", "Email", "Web
// search") — see TYPE_CREATED above for why this stays a lookup of its own.
export function typeCreatedTitle(type) {
  return TYPE_CREATED[panelType(type)] || TYPE_CREATED.event
}

// The caption under an attachment thumbnail: the frame writes "name.ext". A file's own name almost
// always carries its extension already, so this only appends one when it doesn't (a stored document
// saved under a bare name) — never "photo.png.png". A nameless file is captioned by its kind rather
// than by an empty line.
export function attachmentCaption(a) {
  const name = String(a?.name || '').trim()
  const ext = String(a?.extension || '').replace(/^\./, '').toLowerCase()
  if (!name) return ext ? `file.${ext}` : 'Attachment'
  if (!ext || name.toLowerCase().endsWith(`.${ext}`)) return name
  return `${name}.${ext}`
}

// Is this attachment a picture? Its content type when the source knew one (documents do), else its
// extension — email parts arrive with a file name and nothing else.
const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'bmp', 'heic', 'tif', 'tiff']
export function isImageAttachment(a) {
  if (String(a?.contentType || '').startsWith('image/')) return true
  return IMAGE_EXT.includes(String(a?.extension || '').replace(/^\./, '').toLowerCase())
}

// Payload item -> panel row. Returns null for anything undated: a task or waiting-on email with no
// due date is simply absent from this feed (decided on issue #19), never a status-less row.
export function toRow(item, now = Date.now()) {
  if (!item) return null
  // Guard the RAW value: `parseUtc(null)` would hand back the unix epoch, which reads as a
  // legitimately overdue row dated 1970 rather than as the undated row it really is.
  if (item.dueUtc === null || item.dueUtc === undefined || item.dueUtc === '') return null
  const due = parseUtc(item.dueUtc)
  const type = panelType(item.type)
  // An event walks its own ladder — upcoming ▸ due soon ▸ now ▸ past — and is never `overdue`
  // (busy-my-web#238). Everything else on this rail IS a deadline and keeps `dueStatus`.
  const isEvent = type === 'event'
  // WHEN THE EVENT IS OVER — what gives "Now" a window rather than an instant (busy-main-bff#18).
  // The feed sends `endUtc` for a TIMED event (and deliberately none for an all-day one, whose day
  // this app ends itself — see `eventBounds`); `endDatetime` is the calendar module's own spelling,
  // accepted because an event row assembled client-side out of a calendar payload uses that name.
  // Null when neither is there — a pre-#18 cached payload, or an event the BFF has no end for —
  // and `eventBounds` then falls back to the ±1min grace around the start.
  const eventEnd = item.endUtc ?? item.endDatetime ?? null
  // …and an all-day event says so, because that is what says when it is OVER: its start is local
  // midnight, and without the flag the row would call today's all-day event past from 00:01 (#239).
  // …and a DATE-ONLY task (lib/taskDue.js) is judged at the END of its day: its `dueUtc` is the
  // caller-local midnight the BFF anchored it to, and it is not late at 00:01.
  const status = isEvent
    ? eventStatus(due, { end: eventEnd, now, allDay: !!item.isAllDay })
    : dueStatus(item.isAllDay && due ? endOfDay(due) : due, now)
  if (!status) return null

  // Notes and tasks written in the SmartBox are stored as escaped HTML — "&lt;p&gt;q&lt;/p&gt;" has
  // to read as "q". Done here rather than in the BFF: it's a display concern, and the payload is
  // shared with everything else that reads `timeline/global`.
  const detail = toPlainText(item.detail)
  // THE ROW'S DATE IS ITS DUE DATE (issue #217, Saffet: "You can see the creation date on all of
  // them it is the due date and time we want to see at the bottom of each one").
  //
  // This reverses what #65 recorded here — that the stamp is WHEN THE THING WAS MADE, read off the
  // frame stamping a "Task" row "Jul 24 / 10:24 am" while the item itself was due "Today, 8pm". That
  // reading held while this feed was a record of what had happened. It is not one any more: since
  // #208 every row in it is something DUE or already LATE, it is ordered on a due-date axis (#108),
  // and it is capped by due date (#222). A creation instant on such a row is a second date that
  // answers a question nobody looking at this list is asking — and, sitting where the eye reads the
  // row's date, it answers the question they ARE asking with the wrong number.
  //
  // The fallback survives, inverted and now unreachable in practice: a row without a due instant
  // never gets built at all (the guard above), so `createdUtc` is only ever the last resort for a
  // payload shape that does not exist yet, rather than losing a date entirely.
  const { date, time } = timelineDateTime(due || item.createdUtc)

  const amount = item.isAllDay ? '' : dueAmount(due, now)
  // Every email row in this feed is a WAITING-ON email — the BFF only puts an email here when its
  // WaitingOn flag is set — so while it still has time left it reads "Waiting on" (blue), not
  // "Upcoming". Once its date has passed, being late is the more urgent fact and "Overdue" wins.
  // This is display only: `status` below stays the time-based one, because that is what groups and
  // orders the feed (Overdue -> Due soon -> Upcoming).
  const shown = type === 'message' && status !== 'overdue' ? 'waiting' : status
  const meta = statusMeta(shown)

  return {
    id: item.id,
    type,
    // The item's OWN text leads the row; the kind ("Task", "Email") is the small line under it.
    // Falls back to the detail, then to the kind, so the first line is never blank.
    title: toPlainText(item.title) || detail || typeTitle(type),
    // The kind-line under the title: "Task" / "Event" / "Note" (the frame's wording).
    typeLabel: typeCreatedTitle(type),
    // The row's own body text (an event's location/participants, an email's preview, a task's
    // content), already un-escaped. Nothing shows it at rest — it is what the CLICKED state's
    // body-preview line reads (Figma 40007749:354365), and the title only borrows it as a fallback.
    detail,
    // The exact DUE date and time, stacked as the Figma "Date and time" variant draws it (#217).
    date,
    // All-day events have no meaningful clock time — they're due for the whole day.
    time: item.isAllDay ? 'All day' : time,
    // How far past due, or how long is left — live, because it's measured against the ticking `now`.
    // Not for an all-day event: its "due" instant is just local midnight, so "10h ago" would be
    // measuring against a time the event never had.
    dueAmount: amount,
    status,
    // WHEN the thing itself happens, as the calendar detail line reads it: "Today, 8pm". The same
    // due instant as the stamp above, in the frame's own relative wording — this is the line the
    // panel draws at the BOTTOM of the row, and since #217 it is the only date a feed row shows.
    // An all-day event has no clock of its own: its "due" instant is only local midnight, so the line
    // names the day and says so ("Today, All day") instead of inventing a 12am.
    schedule: item.isAllDay ? [scheduleDay(due, now), 'All day'].filter(Boolean).join(', ') : scheduleText(due, now),
    // …and the EXACT instant behind that wording, for the hover tooltip the frames give it
    // ("Aug 18, 3 pm" — 2026-09-15 parity audit F2-7, busy-my-web#501): "Today, 8pm" is the useful
    // reading, and the precise date is one hover away rather than gone. The reminder's own instant
    // where the row is stamped at one (busy-main-bff#501 sends `remindUtc` on task rows).
    scheduleExact: factStamp(due || reminderInstant(item) || null),
    // How many checklist items a task carries; 0 for everything else (its detail line is dropped).
    checklistCount: Number(item.checklistCount) || 0,
    // What the bell says: its colour, its label, and the sentence behind it (tooltip + aria-label).
    statusKey: shown,
    statusLabel: meta.label,
    statusColor: meta.color,
    // The status DETAIL LINE: the frame's wording, in the frame's own token colour (which is not the
    // softer chip colour the Ongoing list uses — see dueStatus).
    // An event says the whole lifecycle here — "In 3 days" / "Tomorrow, 6:45am" / "In 1h 38m" /
    // "Now" / "Ended 38m ago" / "Past · 26 Aug, 6:45am" (busy-my-web#239) — and the sentence is
    // already a sentence, so the bell's tooltip reads the same words rather than a second wording.
    statusText: isEvent ? eventLabel(due, { end: eventEnd, now, allDay: !!item.isAllDay }) : statusText(shown, amount),
    statusTokenColor: statusTokenColor(shown),
    statusTooltip: isEvent ? eventLabel(due, { end: eventEnd, now, allDay: !!item.isAllDay }) : statusExplanation(shown, amount),
    timelineId: item.timelineId || null,
    // WHICH ONGOING the row's thing belongs to, when the payload knows (busy-my-web#383): opening a
    // task from this feed should put its story in the middle behind it, and the row is the only
    // thing the panel holds at the moment of the click. Null for a row with no ongoing — a
    // standalone task, an event, a plain email — and the middle is then left exactly as it was.
    ongoingId: item.ongoingId || null,
    route: item.route || '',
    // What is attached to the thing, for the CLICKED state's thumbnail strip (Figma
    // 40007749:354365). The payload carries a document REFERENCE, never the picture itself — the
    // feed is cached, and the panel fetches a thumbnail only when a row is actually opened. An old
    // BFF sends no `attachments` at all and the strip simply isn't drawn.
    attachments: (item.attachments || [])
      .filter(a => a && (a.documentId || a.name))
      .map(a => ({
        id: a.documentId || null,
        name: a.name || '',
        // The caption is the frame's "name.ext": the name usually carries it already, and this is
        // what fills it in for a stored file whose name does not.
        extension: String(a.extension || '').toLowerCase(),
        contentType: a.contentType || '',
        hasThumbnail: a.hasThumbnail !== false,
      })),
    // Who a waiting-on email is from. Emails only — the BFF sends nothing for a task or an event,
    // which have no sender, and an empty string keeps the line off those rows.
    senderName: item.senderName || '',
    // Is this row an EVENT as the payload DECLARED it (busy-my-web#266)? Not `type === 'event'`,
    // which is also what `panelType` falls back to for a row of no known kind at all: that fallback
    // is about the icon and the colour, and an unknown row must not inherit an event's standing in
    // the feed on the strength of a guess. Only what the BFF actually called an event is one here.
    isEvent: String(item.type || '').toLowerCase() === 'event',
    dueAt: due.getTime()
  }
}

// ── DERIVED LIFECYCLE ROWS: "Reminder due" / "Due time passed" (busy-my-web#410) ────────────────
//
// The feed used to say only that a thing EXISTS and is late — the entity's own row, restyled by its
// due status. Figma "Home page-step 9" (40000999:68095) reads the rail as a CHRONOLOGY instead: the
// moment a reminder fires and the moment a deadline passes are each their own entry, stamped at the
// instant they happened and captioned with how long ago that was. So two rows are derived here from
// instants the item already carries, per item:
//
//   · "Reminder due"     — at the item's reminder instant, once it is behind `now`
//   · "Due time passed"  — at a TASK's due instant, once it is behind `now`
//
// They are derived, not fetched: nothing new is stored, and the pair disappears the moment the task
// completes because the BFF stops sending the item at all (GlobalTimelineBuilder skips Status ==
// Done) — plus the defensive `isDoneItem` guard below for a payload that does send one.
//
// The row SHAPE is the feed's own, so the panel draws it with no new branch: the item's title leads
// (semibold), the kind-line under it is the lifecycle caption, and the status line is the elapsed
// label ("2h ago") in the lifecycle's colour. What differs is `scheduleLabel` — the labelled date
// group at the bottom of the row reads "Reminder" on a reminder row instead of "Due" — and
// `entityId`, which is what `rowOpenTarget` opens (the row's own `id` is suffixed so two rows about
// one task are still two distinct keys).
//
// SCOPE — a due-passed row is a TASK's. An event is not late when its start goes by, it is `past`
// and its own ladder says so (busy-my-web#238/#239); a waiting-on email reads "Waiting on a reply"
// (its date is when a REPLY is expected, not a deadline the user missed). Reading either as a
// missed deadline is exactly the wording those two issues removed. A reminder row is not so
// restricted: any module can carry a reminder, and a reminder firing means the same thing on all of
// them.
export const LIFECYCLE_KINDS = {
  reminder: { label: 'Reminder due', scheduleLabel: 'Reminder', status: 'reminder' },
  duePassed: { label: 'Due time passed', scheduleLabel: 'Due', status: 'overdue' },
}

// WHICH FIELD CARRIES THE REMINDER. The entity modules spell it `remindDate` (SmartBox writes
// `/remindDate`, notes read `remindDate || reminderDate`); the global feed's own payload spells its
// instants `<thing>Utc`. `v1/timeline/global` does NOT send one today — see the issue's "missing BFF
// field" note — so `remindUtc` is read first as the name it should arrive under, and the entity
// spellings are accepted after it so a row assembled client-side out of a task payload derives the
// same way.
const REMINDER_FIELDS = ['remindUtc', 'reminderUtc', 'remindDate', 'reminderDate']

export function reminderInstant(item) {
  for (const f of REMINDER_FIELDS) {
    const v = item?.[f]
    if (v === null || v === undefined || v === '') continue
    const d = parseUtc(v)
    if (d && !isNaN(d.getTime())) return d
  }
  return null
}

// Is this payload item already DONE? The endpoint never sends one (its builder skips completed
// tasks), so this only ever fires for a payload shape that does not exist yet — but a lifecycle row
// is a claim that something is still waiting on the user, and it must not be made about finished
// work. Accepts the three spellings the platform uses for the same fact.
export function isDoneItem(item) {
  if (item?.isCompleted === true || item?.isDone === true) return true
  const status = String(item?.status || '').toLowerCase()
  return status === 'done' || status === 'completed'
}

// One derived row. Returns null when the instant is unusable or still ahead of `now` — a lifecycle
// row is a record of something that has ALREADY happened, never a prediction.
function lifecycleRow(item, kind, instant, now) {
  const meta = LIFECYCLE_KINDS[kind]
  if (!meta || !instant || isNaN(instant.getTime())) return null
  const at = instant.getTime()
  if (at > now) return null
  const type = panelType(item.type)
  const { date, time } = timelineDateTime(instant)
  // "2h ago" / "now" — the elapsed label the frame prints under the title. Always a past amount
  // here (the guard above), so it always reads "… ago" unless it happened within the minute.
  const amount = dueAmount(instant, now)
  const statusKey = meta.status
  const chip = statusMeta(statusKey)
  return {
    id: `${item.id}:${kind}`,
    // The entity the row is ABOUT — what "Open" opens (see rowOpenTarget). The row's own id is
    // suffixed so the panel's per-row key stays unique.
    entityId: item.id ?? null,
    // Which lifecycle moment this row is, for anything that needs to tell a derived row from the
    // entity row it was derived from (the panel's tests, future filtering). Absent on entity rows.
    lifecycle: kind,
    type,
    title: toPlainText(item.title) || typeTitle(type),
    // The kind-line under the title is the MOMENT, not the module: this row is not "a Task", it is
    // "the moment that task's reminder went off".
    typeLabel: meta.label,
    detail: toPlainText(item.detail),
    date,
    time,
    dueAmount: amount,
    status: statusKey,
    // The labelled date group at the bottom of the row — "Reminder" on a reminder row, so it does
    // not announce a due date the row is not about.
    scheduleLabel: meta.scheduleLabel,
    schedule: scheduleText(instant, now),
    scheduleExact: factStamp(instant),
    // A derived row is a marker, not the item: the checklist count, the attachments and the sender
    // stay on the entity's own row rather than being repeated once per lifecycle moment.
    checklistCount: 0,
    attachments: [],
    senderName: '',
    statusKey,
    statusLabel: chip.label,
    statusColor: chip.color,
    // The frame's own caption: how long ago it happened, and nothing else — the row already says
    // WHAT happened on the line above, and saying it twice a line apart is what #217 took out of
    // this row for the date.
    statusText: amount,
    statusTokenColor: statusTokenColor(statusKey),
    statusTooltip: amount ? `${meta.label} ${amount}` : meta.label,
    timelineId: item.timelineId || null,
    ongoingId: item.ongoingId || null,
    route: item.route || '',
    // Never an event in the feed's sense (`isUpcomingEvent` must not pick these up): a lifecycle row
    // is always behind `now`, and it is a moment rather than an appointment.
    isEvent: false,
    dueAt: at,
  }
}

/** The lifecycle rows one payload item yields — 0, 1 or 2, oldest instant first. */
export function lifecycleRows(item, now = Date.now()) {
  if (!item || item.id === null || item.id === undefined || isDoneItem(item)) return []
  const out = []
  const remind = lifecycleRow(item, 'reminder', reminderInstant(item), now)
  if (remind) out.push(remind)
  // Tasks only — see the SCOPE note above.
  if (panelType(item.type) === 'task' && item.dueUtc) {
    const due = lifecycleRow(item, 'duePassed', parseUtc(item.dueUtc), now)
    if (due) out.push(due)
  }
  return out.sort((a, b) => a.dueAt - b.dueAt)
}

/** Every lifecycle row a payload yields, newest instant first (the feed's own axis). */
export function buildLifecycleRows(items, now = Date.now()) {
  return (Array.isArray(items) ? items : [])
    .flatMap(i => lifecycleRows(i, now))
    .sort((a, b) => b.dueAt - a.dueAt)
}

// Which feed rows ARE an entity of their own, and the SmartBox menu each opens under. The same
// three kinds — and the same `menu` words — the story rail's `editOf` uses (lib/storySteps.js), so
// one vocabulary opens a task wherever it is clicked.
const ENTITY_MENU = { task: 'tasks', note: 'notes', event: 'events' }

// The thread reader's own address — the same one the story's offers build (lib/storyOffers), kept
// here as the ONE fallback a routeless email row needs (busy-my-web#386).
const emailRoute = (row) => (row?.type === 'message' && row?.timelineId ? `/emails/timeline/${row.timelineId}` : '')

/**
 * What a row's "Open" should DO (issue #92).
 *
 * A task, a note and an event belong in the universal input box — the same `smartbox-edit-entity`
 * path the briefing and the story rail take. The feed used to push `row.route` for every kind, and
 * the BFF hands a task raised out of an email the route of the EMAIL it came from: clicking Open on
 * a task took the user to the emails page, which is what Fatemeh reported. A route is now only ever
 * followed for the kinds that have nowhere else to go (an email thread, a web search).
 *
 * Returns null when there is nothing to open, so the caller can leave the button off entirely
 * rather than draw one that does nothing.
 */
export function rowOpenTarget(row) {
  if (!row) return null
  const menu = ENTITY_MENU[row.type]
  // An email thread is a PAGE, and it has one whether or not the payload named a route: the thread
  // reader is `/emails/timeline/:timelineId`, which is the same address the story's offers build
  // (lib/storyOffers). Without this fallback a waiting-on email row — and since #108 the newest row
  // is the BOTTOM one, so this is exactly "the last row" — drew no Open link at all and could not
  // be opened from the feed (busy-my-web#386). A row with neither a route nor a timeline id still
  // returns null: no target, no dead button.
  if (!menu) {
    const route = row.route || emailRoute(row)
    return route ? { kind: 'route', route } : null
  }
  // The box prefills from the opener while it loads the entity behind the id — an event dates itself
  // from `startDatetime`, a task and a note from `dueDate` (see SmartBox's onEditEntity).
  const when = row.dueAt ? new Date(row.dueAt).toISOString() : ''
  return {
    kind: 'entity',
    // …and WHICH STORY to put in the middle behind it (busy-my-web#383, Saffet: "I should see the
    // task opened in the timeline as it is now but also see the story in the middle"). Carried
    // beside the opener rather than inside it because it is the CALLER's business — the box only
    // reads `edit` — and null when the row belongs to no ongoing, which leaves the middle alone.
    ongoingId: row.ongoingId || null,
    edit: {
      menu,
      // The ENTITY's id, which is the row's own id everywhere except on a derived lifecycle row —
      // there `id` carries a ":reminder"/":duePassed" suffix so two rows about one task stay two
      // distinct keys, and opening it must still open the task (busy-my-web#410).
      id: row.entityId || row.id || null,
      title: row.title || '',
      content: row.detail || '',
      // The box keeps the ongoing on its `editingRef`, which is what the rail's "Created from"
      // line and the breadcrumb both read.
      ...(row.ongoingId ? { ongoingId: row.ongoingId } : {}),
      ...(menu === 'events' ? { startDatetime: when } : { dueDate: when }),
    },
  }
}

// WHAT THE FEED SHOWS AT ALL — the last five DUE and OVERDUE rows (issue #208, settled by #222).
//
// TWO cuts are made here, and both are Saffet's:
//
//   * the STATUS cut — a row must be `overdue` (its instant has passed) or `dueSoon` (its instant is
//     inside the next hour, which is what this app calls "due"; the vocabulary is @/lib/dueStatus).
//     `upcoming` is not due yet and does not reach the panel. Note this is the row's TIME-based
//     status, not the wording an email row shows: a waiting-on email still reads "Waiting on", but
//     it only appears once it is actually due or late, like everything else. This cut has never
//     moved since #208.
//
//   * the COUNT cut — FIVE rows, and five is the whole list. #208 shipped this as a `.slice(0, 5)`;
//     it was taken back out on 2026-08-26 for the blur affordance (Pouria: "somehow I need to show
//     previous things as a light blurry so the user can notice he can scroll to top to see more"),
//     on the reasoning that a row the feed never builds cannot be blurred or scrolled to — so the
//     cut moved out of the DATA and into the VIEW, and the panel windowed a full-length feed.
//     Saffet looked at the result and reported it back (#222, "There is more than 5 in the global
//     timeline"), which also answers the question #208 left open: the cap is ABSOLUTE. Five seats,
//     no "see all"; a sixth item is reached from the module it belongs to.
//
// The two are not in tension any more, which is why both can stand: the panel's resting window is
// four whole rows plus HALF of the one above them (@/lib/timelineCap's `restingHeight`), so a
// five-row feed rests showing exactly four and a half of its own five. The half-cut top row is
// still the "there is more up here" affordance — it is now honest about how much more there is
// (half a row), and reaching for it opens the window to the space the panel has (#223) rather than
// into a history the cap says should not exist.
//
// WHICH five: the ordering below is by descending due instant, and the slice is taken off the top of
// that — so what falls away is the OLDEST misses at the bottom of the rail, in the order the list is
// actually drawn in. "Last five" by DUE date, never by creation date; the whole rail is a due-date
// axis (#108), and #217 has since made the row's own stamp say so too.
//
// A COMPLETED item leaves on its own and at once: the BFF never builds a row for a done task
// (GlobalTimelineBuilder skips Status == Done / CompletedDate), and @/lib/globalTimelineRefresh
// refetches this feed right after any write that can change it — so completing a task removes its
// row without a reload, and the row below it moves up into the freed fifth seat.
// …with the event's own two names for the same side of the line (busy-my-web#238): an event that is
// happening, or has happened, is exactly what an overdue task is to this cut — something at or
// behind the present moment that the rail must still show. It is only the WORD "overdue" that an
// event never wears, and dropping past events out of the feed instead would answer Saffet's "after
// it passes it has passed" by hiding it.
// …and the ONE exception to the status cut is `isUpcomingEvent` below (busy-my-web#266): an event
// still ahead of its time takes a seat the due rows left empty, because an appointment is not a
// deadline and #239's ladder is written to count it down before it happens.
export const DUE_STATUSES = ['overdue', 'dueSoon', 'now', 'past']
export const FEED_ROWS = 5

// Is this row one of the ones the feed keeps? Its own function so the rule reads once and can be
// tested without building a whole feed.
export function isDueOrOverdue(row) {
  return DUE_STATUSES.includes(row?.status)
}

// THE ORDERING RULE (issue #108, Fatemeh's updated timeline-rules section 40007749:345742).
//
// The rail is not three status bands stacked on each other — it is ONE continuous date/time axis,
// which is what its own hover tooltip says it is ("Aug 18, 3 pm", frame 40008316:160280). So the
// rows are placed on that axis and nowhere else: **the bottom of the rail is NOW**, and going UP
// walks away from now into the future.
//
// Top → bottom, the feed therefore runs by DESCENDING due instant — the frame reads
//   Standup meeting (Tomorrow 8am) → Redesign Timeline (Today 10pm) → Dinner with Sarah (Today 8pm)
//   → Buy a new lock (Overdue 1 hour)
// i.e. farthest-out deadline at the top, the thing nearest to now at the bottom, and anything
// already past continues below the present moment.
//
// This reverses the status-grouped, overdue-newest-first order the earlier #49 addendum asked for —
// but its point survives intact: coming DOWN the rail the first overdue row you meet is still the
// one you have just missed, and the older misses are further down, which is exactly where the
// frame's "scroll back" control sits. The status is still computed per row (the bell, the wording);
// it just no longer decides where a row lives.
//
// A row's instant is its own: a task/event/waiting-on email is placed by its DUE time (`dueAt`);
// nothing undated reaches this list at all (toRow drops it), so there is no second key to guess at.
// Equal instants keep payload order — Array#sort is stable, and two things due at the same minute
// have no claim on each other.
/**
 * …and the one exception to the status cut: an EVENT that has not come due yet (busy-my-web#266).
 *
 * Fatemeh: "من یه ایونت ساختم برای یه ساعت دیگه اما گلوبال تایم لاین در مورد اون ایونت چیزی نزاشته"
 * — an event made for about an hour ahead, and the Global timeline said nothing about it, not even
 * after a reload. It is the status cut above: anything more than an hour out is `upcoming`
 * (lib/eventTime), and `upcoming` never reached the panel. The reload could not help, because the
 * row was never in the list to begin with.
 *
 * That cut is Saffet's and it holds — for the things it was written about. #208 is a rule about
 * DEADLINES: a task or a waiting-on email that is not due yet is not today's work. An event is not
 * a deadline. It is an appointment, and the whole point of #239's ladder — "In 3 days ▸ Tomorrow,
 * 6:45am ▸ In 1h 38m ▸ Now" — is that the feed says how far away it is BEFORE it arrives. Half that
 * ladder could never appear on the surface it was written for.
 *
 * Two things keep this from re-opening #208:
 *   · the reach is the BFF's, not this file's — the endpoint only sends events inside today plus
 *     the near-upcoming window (GlobalTimelineBuilder's NearUpcomingHours, 12h), so "upcoming
 *     event" already means "later today or tonight/tomorrow morning", never next week;
 *   · an upcoming event takes only a SPARE seat: the five seats are filled with due and overdue
 *     rows first, exactly as #222 settled, and only what is left over goes to the nearest events
 *     still ahead. Nothing that is due or late is ever displaced by something that is not.
 */
export function isUpcomingEvent(row) {
  return !!row?.isEvent && row?.status === 'upcoming'
}

export function buildGlobalTimelineRows(items, now = Date.now()) {
  const list = Array.isArray(items) ? items : []
  const rows = list
    .map(i => toRow(i, now))
    .filter(Boolean)
    .sort((a, b) => b.dueAt - a.dueAt)

  const kept = rows.filter(isDueOrOverdue).slice(0, FEED_ROWS)
  let spare = FEED_ROWS - kept.length
  if (spare <= 0) return kept
  // The NEAREST of them: the list runs farthest-out first, so the ones about to happen are its
  // tail. They stay above everything kept — an upcoming instant is further out than any due or
  // overdue one by definition — so the axis is still one continuous descending line.
  const events = rows.filter(isUpcomingEvent).slice(-spare)
  spare -= events.length
  // …and what is STILL spare after that goes to the derived lifecycle rows — the moments a reminder
  // fired and a deadline passed (busy-my-web#410). They queue BEHIND both cuts on purpose: #222's
  // five seats belong to what is due or late, #266's leftovers to the appointments about to happen,
  // and a derived row must not displace either — it is a second thing said about an item the feed
  // is (or is not) already showing. `buildLifecycleRows` runs newest-first, so the ones taken are
  // the moments nearest to now.
  //
  // …but "Due time passed" is never a second thing about a TASK whose own row is already on the
  // rail (busy-my-web#545, Saffet live in Slack: "the task is showing twice ... if it is overdue").
  // A task only ever earns a `duePassed` moment once its due instant is behind `now` — which is
  // exactly `dueStatus`'s `overdue` cut (@/lib/dueStatus) — and `spare` is only ever positive when
  // EVERY due/overdue row already fit in `kept` (the five seats are cut to the count of due/overdue
  // rows first). So whenever this branch can run at all, the task the `duePassed` row would be about
  // is already sitting in `kept` under its own title, and the two rows read as one task said twice.
  // A `reminder` moment has no such overlap — it fires at its OWN earlier instant, a fact `kept`
  // never states — so only `duePassed` rows are filtered out here.
  const keptIds = new Set(kept.map(r => r.id))
  const life = spare > 0
    ? buildLifecycleRows(list, now).filter(r => r.lifecycle !== 'duePassed' || !keptIds.has(r.entityId)).slice(0, spare)
    : []
  if (!life.length) return [...events, ...kept]
  // Back onto the one descending axis. Stable sort with the entity rows listed first, so a
  // "Due time passed" row — which shares its task's exact due instant — sits directly UNDER the
  // task's own row rather than above it.
  return [...events, ...[...kept, ...life].sort((a, b) => b.dueAt - a.dueAt)]
}

/**
 * WHERE THE FEED RESTS — the row that should be the LAST one visible when the panel opens (#108).
 *
 * With the axis reversed, the top of the list is the farthest-out deadline and "now" has moved down
 * into it, so opening at scroll 0 would rest the eye on next week while today's missed task sat
 * below the fold. The resting view therefore ends AT NOW: the first overdue row — the one just past
 * the present moment, the thing you have only just missed — is the last row on screen, and the older
 * misses are further down, which is the direction the frame's "scroll back" control points.
 *
 * With nothing overdue the whole feed is still ahead of the user, so the nearest thing to now is the
 * bottom row and that is what it rests on. Returns -1 for an empty feed: there is nothing to anchor.
 */
export function restAnchorIndex(rows) {
  const list = Array.isArray(rows) ? rows : []
  if (!list.length) return -1
  // A past (or in-progress) EVENT is on the same side of "now" as an overdue task, so it anchors the
  // rest the same way — the boundary is the present moment, not the word on the row (#238).
  const firstOverdue = list.findIndex(r => r?.status === 'overdue' || isPastEventStatus(r?.status))
  return firstOverdue === -1 ? list.length - 1 : firstOverdue
}

/**
 * …and WHERE IN THE PANEL that anchor row rests (busy-my-web#135).
 *
 * #108 put the anchor row flush against the panel's BOTTOM edge, which reads as the rail being
 * scrolled "all the way down": the nearest-to-now boundary is the last thing on screen and the
 * overdue context behind it is entirely below the fold. Saffet asked for it to rest higher, so the
 * boundary row now sits at ~70% of the panel's height — what has just been missed stays visible
 * underneath it, and there is still more upcoming above.
 *
 * Only the resting OFFSET changes: the row that anchors, the ordering, and the once-per-feed rule
 * are all exactly #108's.
 */
export const REST_ANCHOR_FRACTION = 0.7

/**
 * How much further to scroll after the anchor row has been brought to the bottom edge, to leave it
 * at `fraction` of the panel height instead. Pure arithmetic so the offset can be tested without a
 * layout: scrolling DOWN by the remaining 30% of the viewport moves the row up by the same 30%.
 *
 * Returns 0 for a viewport with no measurable height (jsdom, a panel that has not laid out yet), so
 * the caller's `scrollTop +=` is a no-op rather than a NaN. The browser clamps the result to the
 * scrollable range by itself, so an anchor with less than 30% of a screen below it simply ends up as
 * high as the content allows.
 */
export function restScrollShift(viewportHeight, fraction = REST_ANCHOR_FRACTION) {
  const h = Number(viewportHeight)
  if (!Number.isFinite(h) || h <= 0) return 0
  const f = Number.isFinite(Number(fraction)) ? Math.min(Math.max(Number(fraction), 0), 1) : REST_ANCHOR_FRACTION
  return Math.round(h * (1 - f))
}

// ── THE FEED STANDS DOWN FOR A GLOBAL CONVERSATION (Saffet, 2026-10-21) ──────────────────────────
//
// "When I am on a web search and selecting extractions the right hand Timeline shows the new
// selected Extractions, photos. But also the Global timeline at the bottom. The global timeline
// needs to be removed when you are having a global conversation."
//
// On Home the rail stacks the live search session (ResearchTimelineEvents: "Search ready", "N items
// selected", the ticked tiles) ABOVE the day's due feed. The feed already stood down while a search
// was merely ARMED (`research/searchOpen`), but the moment the search answered the armed flag
// dropped and the overdue tasks came back underneath the session — two timelines on one rail. A
// conversation with turns on screen is the rail's subject for as long as it lasts; the due feed
// returns when the conversation is closed (no live turns, nothing armed).

/**
 * Whether the rail's due feed is drawn under the current Home state.
 * @param {{ searchOpen?: boolean, liveTurns?: number }} state
 *   searchOpen  a web search is armed (`research/searchOpen`)
 *   liveTurns   how many turns the live research conversation has on screen
 * @returns {boolean}
 */
export function feedShownBesideConversation({ searchOpen = false, liveTurns = 0 } = {}) {
  return !searchOpen && !(Number(liveTurns) > 0)
}
